import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { BetaAccessRequestResponse } from '@/lib/api';

interface AdminRequestsTableProps {
  requests: BetaAccessRequestResponse[];
  isLoading: boolean;
  onSelectRequest: (request: BetaAccessRequestResponse) => void;
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'accepted':
      return 'default';
    case 'rejected':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const DESCRIPTION_TRUNCATE_LENGTH = 120;

function RequestRow({ req, onSelect }: { req: BetaAccessRequestResponse; onSelect: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const hasLongDescription = req.description.length > DESCRIPTION_TRUNCATE_LENGTH;

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onSelect}>
        <TableCell className="font-medium">{req.name}</TableCell>
        <TableCell>{req.email}</TableCell>
        <TableCell>
          <Badge variant={getStatusVariant(req.status)}>{req.status}</Badge>
        </TableCell>
        <TableCell>{formatDate(req.createdAt)}</TableCell>
      </TableRow>
      {req.description && (
        <TableRow className="hover:bg-transparent border-0">
          <TableCell colSpan={4} className="pt-0 pb-3">
            <div className="text-sm text-muted-foreground">
              {expanded || !hasLongDescription ? (
                <span className="whitespace-pre-wrap">{req.description}</span>
              ) : (
                <span>{req.description.slice(0, DESCRIPTION_TRUNCATE_LENGTH)}...</span>
              )}
              {hasLongDescription && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 ml-1 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((v) => !v);
                  }}
                >
                  {expanded ? (
                    <>
                      Less <ChevronUp className="h-3 w-3 ml-0.5" />
                    </>
                  ) : (
                    <>
                      More <ChevronDown className="h-3 w-3 ml-0.5" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function AdminRequestsTable({
  requests,
  isLoading,
  onSelectRequest,
}: AdminRequestsTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (requests.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No requests found.</div>;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Submitted</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((req) => (
            <RequestRow key={req.requestId} req={req} onSelect={() => onSelectRequest(req)} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
